// HMR nudge: messages refactor 2026-04-26
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useAuthStore } from "@/stores/auth";
import { wsUrl } from "@/api/client";
import type { FieldDefinition } from "@/types/template";
import type { ProjectKind } from "@/types/prompt";

// ── Singleton WebSocket Manager ────────────────────────────────────────────
// Lives outside React — no render cycles can kill it

interface SessionResult {
  promptId: string | null;
  tsx: string | null;
  fields: FieldDefinition[];
  rawResult: string | null;
}

// ── Structured chat messages ───────────────────────────────────────────────

export type MessageRole = "user" | "assistant" | "system";
export type MessageStatus = "streaming" | "complete" | "error" | "cancelled";

/**
 * One typed segment of an assistant message, in stream order. The backend
 * emits separate events for answer prose, extended thinking, and tool
 * activity; keeping them as distinct parts lets the console render each
 * Claude Code-style (dim thinking, `● Tool(arg)` headers with `⎿ result`
 * lines) instead of one undifferentiated text blob.
 */
export type MessagePart =
  | { kind: "text"; text: string }
  | { kind: "thinking"; text: string }
  | {
      kind: "tool";
      toolId: string;
      name: string;
      /** Primary argument (file path / command / pattern), set when the
       *  tool input finishes streaming. */
      summary?: string;
      /** Truncated result preview — the `⎿ …` line. */
      resultPreview?: string;
      resultError?: boolean;
      /** True once the tool_result arrived. */
      done?: boolean;
    };

export interface Message {
  /** Stable id. User messages: client-minted UUID. Assistant: same UUID until
   *  prompt_accepted arrives, then replaced with the server's prompt_id. */
  id: string;
  role: MessageRole;
  /** Plain-text aggregate of the answer prose (kind:"text" parts only).
   *  Kept alongside `parts` because session-restore logic and older cached
   *  transcripts key off it. */
  content: string;
  /** Structured segments in stream order. Absent on messages restored from
   *  pre-parts caches — renderers fall back to `content`. */
  parts?: MessagePart[];
  status: MessageStatus;
  /** Server-side prompt_id for assistant messages; bound on prompt_accepted. */
  promptId?: string;
  /** Error string if status === "error". */
  errorMessage?: string;
  /** ms epoch — for ordering/display only. */
  timestamp: number;
}

interface WsState {
  isConnected: boolean;
  isGenerating: boolean;
  /**
   * Structured chat transcript for the current session. Each user prompt
   * produces a user message + an assistant placeholder; stream_token events
   * grow the latest assistant message in place; resume merges by promptId.
   */
  messages: Message[];
  lastResult: SessionResult | null;
  /**
   * Early preview from `file_written` events — the backend detected that
   * Claude wrote component.tsx to disk during generation and sent us the
   * TSX before the full stream_end. The preview can compile and display
   * this immediately instead of waiting for the generation to finish.
   * Cleared on stream_end (lastResult takes over).
   */
  earlyPreview: SessionResult | null;
  /**
   * Last out-of-band lifecycle phase from the Claude subprocess (hook
   * events). Drives a status indicator above the prompt input. NOT part
   * of the messages list — these are deliberately non-visible in chat.
   * Cleared on stream_end / error / cancelled so the indicator hides.
   */
  currentLifecycle: string | null;
  error: string | null;
  /**
   * True when the messages list was loaded from localStorage on session
   * swap and no new tokens have streamed in since. Used by the Build
   * tab to signal "this is what you had last time you looked."
   */
  transcriptRestoredFromCache: boolean;
  /** True while a JSONL read is in flight from the backend. */
  isLoadingJsonl: boolean;
  /**
   * Live output-token counter for the in-flight generation. Estimated
   * client-side from streamed characters between the backend's per-message
   * usage events, then snapped to the server value when each one arrives.
   * Reset to 0 when a new prompt is sent.
   */
  liveOutputTokens: number;
  /** ms epoch when the current generation started; null while idle. */
  generationStartedAt: number | null;
  /**
   * What the model is doing right now, derived from the most recent stream
   * event: "thinking", "writing", or "tool:<Name>". Drives the status line
   * verb. null while idle.
   */
  currentActivity: string | null;
  /**
   * Max concurrent live Claude subprocesses per user, as reported by the
   * backend on session_ready. Used for displaying cap-related messaging
   * in the UI. Defaults to 3 if not reported.
   */
  maxLiveSessionsPerUser: number;
}

type Listener = () => void;

/** Versioned wire format for the localStorage messages payload. */
const TRANSCRIPT_CACHE_VERSION = 2;

interface CachedTranscriptV2 {
  v: 2;
  messages: Message[];
}

/**
 * localStorage key for persisting a session's structured transcript so it
 * survives page reloads. Keyed by (user_id, session_key) — session_key is
 * either a real root prompt id or the literal "draft" slot used before the
 * first prompt in a new session has completed and been assigned an id.
 *
 * Why not one blob per user: the user can switch between sessions in the
 * sidebar, and each session shows its own console history. A single
 * per-user blob would bleed A's console into B's view after a switch.
 */
function transcriptStorageKey(userId: string | null, sessionKey: string): string {
  return `motionforge-session-transcript:${userId ?? "anon"}:${sessionKey}`;
}

/**
 * Load and parse the cached messages array. Handles three cases:
 *   1. Versioned JSON ({v:2, messages:[...]}) → return messages.
 *   2. Legacy flat string from the pre-refactor format → wrap as a single
 *      "legacy-import" system message so old sessions still display.
 *   3. Missing / parse error → empty array.
 */
function loadCachedTranscript(userId: string | null, sessionKey: string): Message[] {
  if (typeof window === "undefined") return [];
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(transcriptStorageKey(userId, sessionKey));
  } catch {
    return [];
  }
  if (raw == null || raw.length === 0) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      (parsed as { v?: unknown }).v === TRANSCRIPT_CACHE_VERSION &&
      Array.isArray((parsed as CachedTranscriptV2).messages)
    ) {
      return (parsed as CachedTranscriptV2).messages;
    }
  } catch {
    // Fall through to legacy-string handling.
  }

  // Legacy: pre-refactor cache stored a raw transcript string.
  return [
    {
      id: "legacy-import",
      role: "system",
      content: raw,
      status: "complete",
      timestamp: 0,
    },
  ];
}

function saveCachedTranscript(
  userId: string | null,
  sessionKey: string,
  messages: Message[],
) {
  if (typeof window === "undefined") return;
  try {
    const payload: CachedTranscriptV2 = {
      v: TRANSCRIPT_CACHE_VERSION,
      messages,
    };
    window.localStorage.setItem(
      transcriptStorageKey(userId, sessionKey),
      JSON.stringify(payload),
    );
  } catch {
    // Quota exceeded or storage disabled — silently drop
  }
}

function clearCachedTranscript(userId: string | null, sessionKey: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(transcriptStorageKey(userId, sessionKey));
  } catch {
    // ignore
  }
}

/** The pre-session slot used before the first prompt earns a real id. */
const DRAFT_SESSION_KEY = "draft";

/** Generate a UUID. crypto.randomUUID is available in modern browsers. */
function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback — extremely unlikely to be hit, but keeps types/tests happy.
  return `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** How often queued stream events are flushed into React state. ~25fps —
 *  indistinguishable from per-token rendering, at a fraction of the cost. */
const FLUSH_INTERVAL_MS = 40;

/** Trailing-edge delay for persisting the transcript to localStorage while
 *  streaming. Terminal events always persist immediately. */
const PERSIST_THROTTLE_MS = 500;

/** Internal queue entry — a stream event waiting for the next batch flush. */
type PendingStreamEvent =
  | { type: "token"; promptId?: string; kind: "text" | "thinking"; text: string }
  | { type: "tool_start"; promptId?: string; toolId: string; name: string }
  | { type: "tool_input"; promptId?: string; toolId: string; summary: string }
  | {
      type: "tool_result";
      promptId?: string;
      toolId: string | null;
      preview: string;
      isError: boolean;
    };

class ClaudeWsManager {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private promptId: string | null = null;
  private listeners = new Set<Listener>();
  private cachedUserId: string | null = null;
  /**
   * Which session's messages are currently loaded into `_state.messages`.
   * Either a root prompt id or the DRAFT_SESSION_KEY sentinel for pre-session
   * typing. Null until the first `setActiveSession` call — the manager is
   * constructed before any React component has told it which session the
   * user is looking at.
   */
  private activeSessionKey: string | null = null;
  /**
   * Track which MF session was generating when the WS disconnected.
   * On reconnect (session_ready), auto-send resume_generation so the
   * frontend re-attaches to the in-flight background generation task.
   * Set on prompt_accepted, cleared on stream_end/error/cancelled.
   */
  private generatingSessionId: string | null = null;
  /**
   * Stream events waiting to be applied to the messages array. Token and
   * tool events arrive far faster than React should re-render — they queue
   * here and a single flush applies the whole batch every FLUSH_INTERVAL_MS.
   * This is the core of the smooth-streaming fix: one array rebuild + one
   * notify per frame-ish instead of per token.
   */
  private pendingEvents: PendingStreamEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Trailing-edge timer for throttled localStorage persistence. */
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  /** Sum of output_tokens from completed messages (server usage events). */
  private usageBaseTokens = 0;
  /** Streamed chars since the last usage event — drives the live estimate. */
  private charsSinceUsage = 0;
  private _state: WsState = {
    isConnected: false,
    isGenerating: false,
    messages: [],
    lastResult: null,
    earlyPreview: null,
    currentLifecycle: null,
    error: null,
    transcriptRestoredFromCache: false,
    isLoadingJsonl: false,
    maxLiveSessionsPerUser: 3,
    liveOutputTokens: 0,
    generationStartedAt: null,
    currentActivity: null,
  };

  /**
   * Resolve the current activeSessionKey into the backend session_id
   * field to send over WS. Real sessions use their root prompt id;
   * draft state sends null, telling the backend to allocate a new root.
   */
  private backendSessionId(): string | null {
    const k = this.activeSessionKey;
    return k === null || k === DRAFT_SESSION_KEY ? null : k;
  }

  constructor() {
    // No transcript restore here — we don't know which session to restore
    // yet. PromptWorkspace calls setActiveSession() on mount which loads
    // the correct session's messages (or the draft slot).
  }

  private getUserId(): string | null {
    const userId = this.cachedUserId ?? useAuthStore.getState().user?.id ?? null;
    if (userId) this.cachedUserId = userId;
    return userId;
  }

  private persistTranscript() {
    const userId = this.getUserId();
    if (!userId) return;
    const key = this.activeSessionKey ?? DRAFT_SESSION_KEY;
    saveCachedTranscript(userId, key, this._state.messages);
  }

  /**
   * Tell the manager which session the user is currently viewing. Saves the
   * previous session's messages to its localStorage slot, then swaps in
   * the target session's cached messages (or an empty array). Called by
   * PromptWorkspace whenever its sessionId prop changes.
   */
  setActiveSession(sessionId: string | null) {
    const userId = this.getUserId();
    const newKey = sessionId ?? DRAFT_SESSION_KEY;
    if (this.activeSessionKey === newKey) return;

    // Apply any queued stream events to the OUTGOING session's messages
    // before we snapshot them to localStorage.
    this.flushPending();

    // Flush the outgoing session's messages to its own slot before we
    // overwrite _state.messages. Skips on the very first call when
    // activeSessionKey is still null (nothing to flush). Also skip if
    // we're leaving the draft slot — draft is meant to be ephemeral
    // (one-shot use until promoted), so we don't preserve its content
    // when the user explicitly navigates away.
    if (
      this.activeSessionKey !== null &&
      this.activeSessionKey !== DRAFT_SESSION_KEY &&
      userId
    ) {
      saveCachedTranscript(userId, this.activeSessionKey, this._state.messages);
    }

    // Transitioning TO the draft slot means "new session" — always a
    // clean slate. Pre-clear any stale draft content from a previous
    // session that didn't get promoted (e.g., user abandoned a
    // pre-prompt-accepted generation, or hit New Session on an
    // in-flight one).
    if (newKey === DRAFT_SESSION_KEY && userId) {
      clearCachedTranscript(userId, DRAFT_SESSION_KEY);
    }

    const cached = userId ? loadCachedTranscript(userId, newKey) : [];
    this.activeSessionKey = newKey;
    this._state.messages = cached;
    this._state.transcriptRestoredFromCache = cached.length > 0;
    // Browsing the sidebar MUST NOT touch the backend's filesystem. We do
    // NOT auto-load the JSONL here — the user can click "Resume Session"
    // if they want to pull from disk.
    this.notify();

    // BUT: we DO want to attach to any in-flight generation on the
    // server for this session. Sending resume_generation when activating
    // a real session lets the user navigate away mid-generation and come
    // back with the streaming progress restored. Server replies
    // `resume_active` (clear local cache before replay) or
    // `generation_not_found` (no-op). Skip the draft slot since drafts
    // never have a server-side generation.
    if (
      sessionId &&
      this.ws &&
      this.ws.readyState === WebSocket.OPEN
    ) {
      this.send({
        type: "resume_generation",
        session_id: sessionId,
      });
    }
  }

  /**
   * Remove any localStorage transcript entries for THIS user whose session
   * id is not in the given authoritative list. Call this after the
   * server's sessions list resolves so orphaned entries (session deleted
   * from the DB but still cached locally) get cleaned up.
   *
   * The `draft` slot is preserved unless `includeDraft` is true — the
   * draft slot represents in-progress typing the user hasn't submitted
   * yet and is not a "session" from the server's point of view.
   */
  cleanupOrphanedTranscripts(knownSessionIds: Iterable<string>, includeDraft = false) {
    if (typeof window === "undefined") return;
    const userId = this.getUserId();
    if (!userId) return;

    const known = new Set(knownSessionIds);
    const prefix = `motionforge-session-transcript:${userId}:`;
    const toRemove: string[] = [];

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const sessionKey = key.slice(prefix.length);
      if (sessionKey === DRAFT_SESSION_KEY) {
        if (includeDraft) toRemove.push(key);
        continue;
      }
      if (!known.has(sessionKey)) {
        toRemove.push(key);
      }
    }

    for (const key of toRemove) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore quota / disabled storage
      }
    }

    if (toRemove.length > 0) {
      console.debug(
        `[useClaudeSession] GC'd ${toRemove.length} orphaned transcript entries`,
      );
      // If the currently-loaded transcript is now stale (its backing slot
      // was just wiped), blank the in-memory copy so the Build tab stops
      // rendering it. Three cases to handle:
      //
      //   1. activeSessionKey is a REAL session id that isn't in the
      //      known set → the session was deleted; drop back to draft.
      //   2. activeSessionKey is DRAFT and includeDraft=true → the draft
      //      slot was wiped; blank messages but keep the key.
      //   3. activeSessionKey is null (never set) → nothing to do.
      const keyIsOrphanedReal =
        this.activeSessionKey !== null &&
        this.activeSessionKey !== DRAFT_SESSION_KEY &&
        !known.has(this.activeSessionKey);
      const keyIsWipedDraft =
        this.activeSessionKey === DRAFT_SESSION_KEY && includeDraft;

      if (keyIsOrphanedReal || keyIsWipedDraft) {
        if (keyIsOrphanedReal) {
          this.activeSessionKey = DRAFT_SESSION_KEY;
        }
        this._state.messages = [];
        this._state.transcriptRestoredFromCache = false;
        this._state.lastResult = null;
        this.notify();
      }
    }
  }

  /**
   * Convert the draft slot's messages into the real session's slot. Called
   * right after a brand-new session's first prompt completes and earns a
   * root prompt id. Without this, the next `setActiveSession(newPid)` call
   * would load an empty array from the `newPid` key and the user's
   * just-finished first turn would vanish off the screen.
   */
  promoteDraftToSession(sessionId: string) {
    const userId = this.getUserId();
    if (!userId) {
      this.activeSessionKey = sessionId;
      return;
    }
    // Only migrate content if we're currently holding the draft slot.
    // If we're already on a real session, this is a no-op.
    if (this.activeSessionKey !== DRAFT_SESSION_KEY && this.activeSessionKey !== null) {
      return;
    }
    saveCachedTranscript(userId, sessionId, this._state.messages);
    clearCachedTranscript(userId, DRAFT_SESSION_KEY);
    this.activeSessionKey = sessionId;
  }

  get state(): WsState {
    return this._state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    // Create new state object so useSyncExternalStore detects change
    this._state = { ...this._state };
    for (const listener of this.listeners) listener();
  }

  private update(partial: Partial<WsState>) {
    Object.assign(this._state, partial);
    // Persist messages on every change that affects them. Lightweight —
    // localStorage writes are cheap enough at our transcript sizes.
    if ("messages" in partial) {
      this.persistTranscript();
    }
    this.notify();
  }

  // ── Message-array helpers ────────────────────────────────────────────────

  /**
   * Mutate the messages array via a builder function. Replaces the array
   * reference (so React sees the change), persists to localStorage, and
   * notifies subscribers. The builder MUST return the new array — it is
   * called with the current array, not the live reference (don't mutate
   * the input).
   */
  private mutateMessages(builder: (prev: Message[]) => Message[]) {
    const next = builder(this._state.messages);
    this._state.messages = next;
    this.persistTranscript();
    this.notify();
  }

  /** Find index of the latest assistant message that's still streaming. */
  private findOpenAssistantIndex(messages: Message[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]!;
      if (m.role === "assistant" && m.status === "streaming") return i;
    }
    return -1;
  }

  /** Find index of an assistant message by its bound promptId. */
  private findByPromptId(messages: Message[], promptId: string): number {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]!;
      if (m.role === "assistant" && m.promptId === promptId) return i;
    }
    return -1;
  }

  // ── Stream-event batching ────────────────────────────────────────────────
  //
  // Token/tool events queue here and flush into React state in batches.
  // The previous implementation rebuilt the messages array, re-serialized
  // the whole transcript to localStorage, and re-rendered every message on
  // EVERY streamed token — the source of the laggy console. Now: one array
  // rebuild + one notify per FLUSH_INTERVAL_MS, persistence throttled
  // separately.

  private queueEvent(ev: PendingStreamEvent) {
    this.pendingEvents.push(ev);
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = null;
        this.flushPending();
      }, FLUSH_INTERVAL_MS);
    }
  }

  /**
   * Apply every queued stream event to the messages array in one pass.
   * Called on the flush timer, and synchronously before any handler whose
   * correctness depends on the transcript being up to date (prompt_accepted,
   * stream_end, session swaps, …) so event order is preserved.
   *
   * Unchanged messages and parts keep reference identity so React.memo'd
   * message blocks skip re-rendering — only the message actually receiving
   * tokens pays a render.
   */
  private flushPending() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.pendingEvents.length === 0) return;
    const batch = this.pendingEvents;
    this.pendingEvents = [];

    let charCount = 0;
    let lastActivity: string | null = null;

    const next = this._state.messages.slice();
    const clonedIdx = new Set<number>();
    // Clone-on-first-touch per flush; also seed `parts` for messages
    // restored from pre-parts caches so their existing prose isn't lost
    // when new structured tokens append after a resume.
    const cloneAt = (idx: number): Message => {
      if (!clonedIdx.has(idx)) {
        const orig = next[idx]!;
        const parts: MessagePart[] = orig.parts
          ? orig.parts.slice()
          : orig.content.length > 0
            ? [{ kind: "text", text: orig.content }]
            : [];
        next[idx] = { ...orig, parts };
        clonedIdx.add(idx);
      }
      return next[idx]!;
    };

    // Resolve which assistant message an event belongs to, creating a
    // streaming placeholder when none exists (resume replay races, tokens
    // before prompt_accepted — same fallbacks the per-token path had).
    const targetIndex = (promptId?: string): number => {
      if (promptId) {
        const idx = this.findByPromptId(next, promptId);
        if (idx !== -1) return idx;
        next.push({
          id: promptId,
          role: "assistant",
          content: "",
          parts: [],
          status: "streaming",
          promptId,
          timestamp: Date.now(),
        });
        clonedIdx.add(next.length - 1);
        return next.length - 1;
      }
      const idx = this.findOpenAssistantIndex(next);
      if (idx !== -1) return idx;
      next.push({
        id: newId(),
        role: "assistant",
        content: "",
        parts: [],
        status: "streaming",
        timestamp: Date.now(),
      });
      clonedIdx.add(next.length - 1);
      return next.length - 1;
    };

    const findToolPart = (parts: MessagePart[], toolId: string): number => {
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i]!;
        if (p.kind === "tool" && p.toolId === toolId) return i;
      }
      return -1;
    };

    for (const ev of batch) {
      const idx = targetIndex(ev.promptId);
      const msg = cloneAt(idx);
      const parts = msg.parts!;
      switch (ev.type) {
        case "token": {
          charCount += ev.text.length;
          lastActivity = ev.kind === "thinking" ? "thinking" : "writing";
          const last = parts[parts.length - 1];
          if (last && last.kind === ev.kind) {
            parts[parts.length - 1] = { ...last, text: last.text + ev.text };
          } else {
            parts.push({ kind: ev.kind, text: ev.text });
          }
          if (ev.kind === "text") {
            msg.content = msg.content + ev.text;
          }
          if (ev.promptId && !msg.promptId) {
            msg.promptId = ev.promptId;
          }
          break;
        }
        case "tool_start": {
          lastActivity = `tool:${ev.name}`;
          parts.push({ kind: "tool", toolId: ev.toolId, name: ev.name });
          break;
        }
        case "tool_input": {
          const ti = findToolPart(parts, ev.toolId);
          if (ti !== -1) {
            const p = parts[ti] as Extract<MessagePart, { kind: "tool" }>;
            parts[ti] = { ...p, summary: ev.summary || p.summary };
            lastActivity = `tool:${p.name}`;
          }
          break;
        }
        case "tool_result": {
          let ti = ev.toolId ? findToolPart(parts, ev.toolId) : -1;
          if (ti === -1) {
            // No id match — attach to the most recent unfinished tool part.
            for (let i = parts.length - 1; i >= 0; i--) {
              const p = parts[i]!;
              if (p.kind === "tool" && !p.done) {
                ti = i;
                break;
              }
            }
          }
          if (ti !== -1) {
            const p = parts[ti] as Extract<MessagePart, { kind: "tool" }>;
            parts[ti] = {
              ...p,
              resultPreview: ev.preview || p.resultPreview,
              resultError: ev.isError || undefined,
              done: true,
            };
          }
          break;
        }
      }
    }

    this.charsSinceUsage += charCount;
    this._state.messages = next;
    if (lastActivity) this._state.currentActivity = lastActivity;
    // ~4 chars/token is close enough for a live ticker; usage events snap
    // the count to the server truth after each assistant message.
    this._state.liveOutputTokens =
      this.usageBaseTokens + Math.round(this.charsSinceUsage / 4);
    this._state.transcriptRestoredFromCache = false;
    this.schedulePersist();
    this.notify();
  }

  /** Throttled transcript persistence — trailing edge, 500ms. */
  private schedulePersist() {
    if (this.persistTimer) return;
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      this.persistTranscript();
    }, PERSIST_THROTTLE_MS);
  }

  private persistNow() {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    this.persistTranscript();
  }

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN ||
        this.ws?.readyState === WebSocket.CONNECTING) return;

    const token = useAuthStore.getState().token;
    if (!token) {
      this.update({ error: "Not authenticated" });
      return;
    }

    const url = wsUrl(`/ws/session?token=${encodeURIComponent(token)}`);

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.update({ isConnected: true, error: null });
    };

    ws.onmessage = (event) => {
      let msg: Record<string, unknown>;
      try { msg = JSON.parse(event.data as string); } catch { return; }

      // High-frequency stream events go through the batch queue; everything
      // else flushes the queue first so transcript mutations stay ordered
      // relative to the stream (e.g. stream_end must not complete a message
      // that still has tokens waiting in the queue).
      switch (msg.type) {
        case "stream_token":
        case "tool_start":
        case "tool_input":
        case "tool_result":
        case "lifecycle":
        case "usage":
        case "file_written":
          break;
        default:
          this.flushPending();
      }

      switch (msg.type) {
        case "session_ready": {
          const cap = typeof msg.max_live_sessions_per_user === "number"
            ? (msg.max_live_sessions_per_user as number)
            : 3;
          this.update({ maxLiveSessionsPerUser: cap });
          // Auto-resume: if we were generating when the WS dropped,
          // re-attach to the background generation task on the backend.
          if (this.generatingSessionId) {
            console.debug(
              `[useClaudeSession] auto-resuming generation for session ${this.generatingSessionId}`,
            );
            this.send({
              type: "resume_generation",
              session_id: this.generatingSessionId,
            });
          }
          break;
        }
        case "resume_active":
          // Server has buffered events for the session we just activated and
          // is about to replay them. Do NOT clear the messages list — it
          // holds PRIOR turns (loaded from localStorage cache). The
          // stream_token handler will route incoming tokens to the right
          // assistant message by promptId, or push a new message if this is
          // the first time we're seeing that promptId in this tab.
          this.usageBaseTokens = 0;
          this.charsSinceUsage = 0;
          this.update({
            isGenerating: true,
            transcriptRestoredFromCache: false,
            liveOutputTokens: 0,
            generationStartedAt:
              this._state.generationStartedAt ?? Date.now(),
          });
          if (msg.session_id) this.generatingSessionId = msg.session_id as string;
          break;
        case "jsonl_loaded": {
          // Backend read the session JSONL from disk and sent the readable
          // transcript. We don't have structured turn boundaries from this
          // payload, so wrap the whole thing as a single legacy-style
          // system message so the user can at least see their past
          // conversation.
          const content = (msg.content as string) ?? "";
          this.mutateMessages(() => [
            {
              id: "jsonl-import",
              role: "system",
              content,
              status: "complete",
              timestamp: Date.now(),
            },
          ]);
          this.update({
            isLoadingJsonl: false,
            transcriptRestoredFromCache: false,
          });
          break;
        }
        case "jsonl_load_error":
          this.update({
            isLoadingJsonl: false,
            error: (msg.message as string) ?? "Failed to load session history",
          });
          break;
        case "prompt_accepted": {
          if (msg.prompt_id) this.promptId = msg.prompt_id as string;
          if (msg.session_id) this.generatingSessionId = msg.session_id as string;
          const promptId = msg.prompt_id as string | undefined;

          // If this is a brand-new session that started life under the
          // "draft" cache key, promote NOW (not at stream_end) so the
          // cache is keyed by the real session id while tokens are still
          // streaming. Without this, clicking the session in the sidebar
          // mid-generation moves activeSessionKey from "draft" to the
          // real id — but the real id slot is still empty, so the chain
          // synth replaces actual streaming content with summaries.
          const sessionId = msg.session_id as string | undefined;
          if (
            sessionId &&
            this.activeSessionKey === DRAFT_SESSION_KEY
          ) {
            this.promoteDraftToSession(sessionId);
          }

          if (!promptId) break;

          this.mutateMessages((prev) => {
            const last = prev[prev.length - 1];
            // Common path: we just locally pushed a streaming assistant
            // placeholder when the user clicked Send. Bind its promptId
            // and replace the client-minted id with the server's id so
            // future stream_token events with prompt_id route correctly.
            if (
              last &&
              last.role === "assistant" &&
              last.status === "streaming" &&
              !last.promptId
            ) {
              const next = prev.slice();
              next[next.length - 1] = {
                ...last,
                id: promptId,
                promptId,
              };
              return next;
            }
            // Race fallback: no open placeholder (e.g. tokens arrived
            // before prompt_accepted, which shouldn't happen but cheap to
            // guard against). Push a new streaming assistant message.
            return [
              ...prev,
              {
                id: promptId,
                role: "assistant",
                content: "",
                status: "streaming",
                promptId,
                timestamp: Date.now(),
              },
            ];
          });
          break;
        }
        case "stream_start":
          // Flip generating + clear cache flag. Ensure we have an open
          // assistant message; push a placeholder if for some reason
          // there isn't one (shouldn't happen on the normal send path
          // since sendPrompt already pushes one, but keeps us robust to
          // server-driven flows like resume_active).
          this.mutateMessages((prev) => {
            if (this.findOpenAssistantIndex(prev) !== -1) return prev;
            return [
              ...prev,
              {
                id: newId(),
                role: "assistant",
                content: "",
                status: "streaming",
                timestamp: Date.now(),
              },
            ];
          });
          this.update({
            isGenerating: true,
            error: null,
            transcriptRestoredFromCache: false,
            generationStartedAt:
              this._state.generationStartedAt ?? Date.now(),
          });
          break;
        case "stream_token": {
          const text = msg.text as string | undefined;
          if (!text) break;
          // Fall back to the manager's active prompt id when the server
          // event doesn't carry one (the Rust StreamToken isn't tagged) —
          // without this, every live token would create an orphan
          // placeholder that the chain-synth race can wipe out.
          const promptId =
            (msg.prompt_id as string | undefined) ?? this.promptId ?? undefined;
          const kind =
            (msg.kind as string | undefined) === "thinking"
              ? ("thinking" as const)
              : ("text" as const);
          this.queueEvent({ type: "token", promptId, kind, text });
          break;
        }
        case "tool_start": {
          if (typeof msg.tool_id !== "string" || typeof msg.name !== "string") break;
          this.queueEvent({
            type: "tool_start",
            promptId:
              (msg.prompt_id as string | undefined) ?? this.promptId ?? undefined,
            toolId: msg.tool_id,
            name: msg.name,
          });
          break;
        }
        case "tool_input": {
          if (typeof msg.tool_id !== "string") break;
          this.queueEvent({
            type: "tool_input",
            promptId:
              (msg.prompt_id as string | undefined) ?? this.promptId ?? undefined,
            toolId: msg.tool_id,
            summary: typeof msg.summary === "string" ? msg.summary : "",
          });
          break;
        }
        case "tool_result": {
          this.queueEvent({
            type: "tool_result",
            promptId:
              (msg.prompt_id as string | undefined) ?? this.promptId ?? undefined,
            toolId: typeof msg.tool_id === "string" ? msg.tool_id : null,
            preview: typeof msg.preview === "string" ? msg.preview : "",
            isError: msg.is_error === true,
          });
          break;
        }
        case "usage": {
          // Per-message server usage — snap the live estimate to truth.
          if (typeof msg.output_tokens === "number") {
            this.usageBaseTokens += msg.output_tokens;
            this.charsSinceUsage = 0;
            this.update({ liveOutputTokens: this.usageBaseTokens });
          }
          break;
        }
        case "lifecycle":
          // Out-of-band phase ping (hook_pre_tool_use, hook_post_tool_use,
          // etc.). Drives a status indicator above the prompt input;
          // deliberately NOT appended to messages so chat stays clean.
          if (typeof msg.phase === "string") {
            this.update({ currentLifecycle: msg.phase });
          }
          break;
        case "file_written":
          // Backend detected component.tsx on disk during generation.
          // Set earlyPreview so the frontend can compile + show the
          // preview immediately — don't wait for stream_end.
          if (msg.tsx) {
            this.update({
              earlyPreview: {
                promptId: (msg.prompt_id as string) ?? this.promptId,
                tsx: (msg.tsx as string) ?? null,
                fields: (msg.fields as FieldDefinition[]) ?? [],
                rawResult: null,
              },
            });
          }
          break;
        case "stream_end": {
          this.generatingSessionId = null;
          const promptId = msg.prompt_id as string | undefined;
          this.mutateMessages((prev) => {
            let idx = promptId ? this.findByPromptId(prev, promptId) : -1;
            if (idx === -1) idx = this.findOpenAssistantIndex(prev);
            if (idx === -1) return prev;
            const next = prev.slice();
            next[idx] = { ...next[idx]!, status: "complete" };
            return next;
          });
          this.update({
            isGenerating: false,
            earlyPreview: null, // final result supersedes early preview
            currentLifecycle: null,
            currentActivity: null,
            generationStartedAt: null,
            lastResult: {
              promptId: (msg.prompt_id as string) ?? this.promptId,
              tsx: (msg.tsx as string) ?? null,
              fields: (msg.fields as FieldDefinition[]) ?? [],
              rawResult: (msg.raw_result as string) ?? null,
            },
          });
          this.persistNow();
          break;
        }
        case "error": {
          this.generatingSessionId = null;
          const message = (msg.message as string) ?? "Unknown error";
          this.mutateMessages((prev) => {
            const idx = this.findOpenAssistantIndex(prev);
            if (idx === -1) return prev;
            const next = prev.slice();
            next[idx] = {
              ...next[idx]!,
              status: "error",
              errorMessage: message,
            };
            return next;
          });
          this.update({
            isGenerating: false,
            currentLifecycle: null,
            currentActivity: null,
            generationStartedAt: null,
            error: message,
          });
          this.persistNow();
          break;
        }
        case "process_exited":
          this.generatingSessionId = null;
          this.update({
            isGenerating: false,
            currentLifecycle: null,
            currentActivity: null,
            generationStartedAt: null,
          });
          break;
        case "cancelled": {
          this.generatingSessionId = null;
          this.mutateMessages((prev) => {
            const idx = this.findOpenAssistantIndex(prev);
            if (idx === -1) return prev;
            const next = prev.slice();
            next[idx] = { ...next[idx]!, status: "cancelled" };
            return next;
          });
          this.update({
            isGenerating: false,
            currentLifecycle: null,
            currentActivity: null,
            generationStartedAt: null,
          });
          this.persistNow();
          break;
        }
        case "generation_not_found":
          // Backend had no in-flight generation for our resume request —
          // the generation must have completed between disconnect and
          // reconnect. Clear generating state so the UI isn't stuck.
          this.generatingSessionId = null;
          this.update({
            isGenerating: false,
            currentActivity: null,
            generationStartedAt: null,
          });
          break;
      }
    };

    ws.onerror = () => {
      this.update({ isConnected: false });
    };

    ws.onclose = (event) => {
      this.ws = null;
      this.update({ isConnected: false });

      if (event.code === 1002 || event.code === 1008) {
        // Auth failure — stop reconnecting and show error. Don't redirect;
        // let the user navigate away manually. A hard redirect here would
        // interrupt unrelated workflows on other pages.
        this.update({ error: "WebSocket authentication failed. Please log out and back in." });
        return;
      }

      // Reconnect after 3s
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => this.connect(), 3000);
    };
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.update({ isConnected: false });
  }

  send(msg: Record<string, unknown>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.update({ error: "Not connected to server" });
      return;
    }
    this.ws.send(JSON.stringify(msg));
  }

  /**
   * Push the user message + a streaming assistant placeholder into the
   * messages array, so the user sees their input appear instantly without
   * waiting for the server round-trip. The placeholder's id will be replaced
   * with the real prompt_id when prompt_accepted arrives.
   */
  private pushTurnLocally(text: string) {
    const now = Date.now();
    const userMsg: Message = {
      id: newId(),
      role: "user",
      content: text,
      status: "complete",
      timestamp: now,
    };
    const assistantMsg: Message = {
      id: newId(),
      role: "assistant",
      content: "",
      parts: [],
      status: "streaming",
      timestamp: now,
    };
    this.mutateMessages((prev) => [...prev, userMsg, assistantMsg]);
  }

  /** Common state reset when a new turn starts streaming. */
  private beginTurn() {
    this.usageBaseTokens = 0;
    this.charsSinceUsage = 0;
    this.update({
      error: null,
      lastResult: null,
      isGenerating: true,
      liveOutputTokens: 0,
      generationStartedAt: Date.now(),
      currentActivity: null,
    });
  }

  /**
   * Forward the prompt to the backend with the current backend session id,
   * after locally pushing the user's text + an assistant placeholder so it
   * shows up in the console immediately.
   */
  /** Tenant style handling for outgoing generations. "brand" is the server
   *  default so it's never sent; the workspace Settings tab updates this
   *  from the session's editor draft before each send. */
  private _style: {
    mode: "brand" | "client" | "off" | "custom";
    custom: string;
    clientId: string | null;
  } = { mode: "brand", custom: "", clientId: null };

  setStyle(
    mode: "brand" | "client" | "off" | "custom",
    custom: string,
    clientId: string | null = null,
  ) {
    this._style = { mode, custom, clientId };
  }

  /** Per-project model choice from the composer's picker; "" = follow the
   *  user's saved per-kind preference / server default. */
  private _model = "";
  private _provider = "claude";

  setModel(model: string) {
    this._model = model;
  }

  setProvider(provider: string) {
    this._provider = provider;
  }

  private attachStyle(msg: Record<string, unknown>) {
    if (this._provider && this._provider !== "claude") {
      // Honored by the backend only on a session's first prompt; existing
      // sessions stay on their root provider.
      msg.provider = this._provider;
    }
    if (this._model) {
      msg.model = this._model;
    }
    if (this._style.mode === "off") {
      msg.style_mode = "off";
    } else if (this._style.mode === "client" && this._style.clientId) {
      msg.style_mode = "client";
      msg.style_client_id = this._style.clientId;
    } else if (this._style.mode === "custom" && this._style.custom.trim()) {
      msg.style_mode = "custom";
      msg.style_override = this._style.custom;
    }
  }

  sendPrompt(
    text: string,
    projectKind?: ProjectKind,
    referenceAssetIds?: string[],
    contextEntries?: unknown[],
  ) {
    this.pushTurnLocally(text);
    this.beginTurn();
    const msg: Record<string, unknown> = {
      type: "prompt",
      text,
      session_id: this.backendSessionId(),
    };
    this.attachStyle(msg);
    // project_kind is honored by the backend ONLY on the first prompt of a
    // new session (session_id null); existing sessions inherit from their
    // root, so don't bother sending it there.
    if (projectKind && this.backendSessionId() === null) {
      msg.project_kind = projectKind;
    }
    // Context-ledger entries staged in the Context tab before the first
    // generation. Like project_kind, only meaningful on a NEW session —
    // existing sessions edit the ledger via the REST endpoint instead.
    if (
      contextEntries &&
      contextEntries.length > 0 &&
      this.backendSessionId() === null
    ) {
      msg.context_entries = contextEntries;
    }
    // Reference images are attached PER PROMPT (unlike project_kind) — the
    // model views them for this specific turn. Sent on every prompt they're
    // present on, new session or not.
    if (referenceAssetIds && referenceAssetIds.length > 0) {
      msg.reference_asset_ids = referenceAssetIds;
    }
    this.send(msg);
  }

  sendIterate(
    text: string,
    parentId: string,
    editedCode?: string,
    referenceAssetIds?: string[],
    fieldValues?: Record<string, unknown>,
  ) {
    this.pushTurnLocally(text);
    this.beginTurn();
    const msg: Record<string, unknown> = {
      type: "iterate",
      text,
      parent_id: parentId,
      session_id: this.backendSessionId(),
    };
    this.attachStyle(msg);
    // Only include edited_code if the user actually changed the TSX.
    // This avoids re-sending the full code on every iterate when the
    // user didn't touch it — saving tokens.
    if (editedCode !== undefined) {
      msg.edited_code = editedCode;
    }
    if (referenceAssetIds && referenceAssetIds.length > 0) {
      msg.reference_asset_ids = referenceAssetIds;
    }
    // Field values the user changed by hand in the Fields tab. The backend
    // embeds them in the prompt so the model designs around them and adopts
    // them as the new defaults instead of reverting to its old values.
    if (fieldValues && Object.keys(fieldValues).length > 0) {
      msg.field_values = fieldValues;
    }
    this.send(msg);
  }

  /**
   * Cancel the in-progress generation on the active session. Backend kills
   * the Claude subprocess for that specific MF session; its
   * claude_session_id is preserved so the next prompt on the same MF
   * session respawns via --resume.
   */
  sendCancel() {
    if (!this._state.isGenerating) return;
    this.flushPending();
    this.send({ type: "cancel", session_id: this.backendSessionId() });
    this.update({
      isGenerating: false,
      currentActivity: null,
      generationStartedAt: null,
    });
  }

  /**
   * Send a message to the Claude subprocess WHILE it's generating.
   * This allows the user to correct or redirect Claude mid-generation
   * instead of having to Stop and re-prompt. The message is injected
   * as a new user turn into the live subprocess conversation. Only
   * works when isGenerating is true — otherwise use sendPrompt.
   */
  sendInterrupt(text: string) {
    if (!this._state.isGenerating) return;
    this.pushTurnLocally(text);
    this.send({
      type: "interrupt",
      text,
      session_id: this.backendSessionId(),
    });
  }

  /**
   * End the active session to free server resources but keep the
   * conversation available for later --resume. Scoped to the currently
   * viewed session only.
   */
  sendEndSession() {
    this.send({ type: "end_session", session_id: this.backendSessionId() });
  }

  /**
   * Ask the backend to read the Claude Code JSONL for the currently-active
   * session from disk. Zero token cost — just a file read. Does nothing
   * if we're on the draft slot (no claude_session_id yet).
   */
  loadJsonl() {
    const backendId = this.backendSessionId();
    if (!backendId) return;
    this.update({ isLoadingJsonl: true, error: null });
    this.send({ type: "load_jsonl", session_id: backendId });
  }

  /**
   * Abandon the current session and pivot to a fresh draft. Tells the
   * backend to forget (kill + clear dormant id) ONLY the currently active
   * session, not any others. Existing sessions in the sidebar keep their
   * messages and claude_session_ids intact.
   */
  sendNewSession() {
    const backendId = this.backendSessionId();
    this.send({ type: "new_session", session_id: backendId });

    const userId = this.getUserId();
    if (userId) clearCachedTranscript(userId, DRAFT_SESSION_KEY);
    this.activeSessionKey = DRAFT_SESSION_KEY;
    this.update({
      messages: [],
      lastResult: null,
      error: null,
      transcriptRestoredFromCache: false,
    });
  }

  /**
   * Directly set the messages array for the current session. Used to
   * populate a synthesized transcript from DB data when localStorage is
   * empty (e.g. after a hard refresh or new device).
   */
  setMessages(messages: Message[]) {
    this._state.messages = messages;
    const userId = this.getUserId();
    const key = this.activeSessionKey;
    if (userId && key) {
      saveCachedTranscript(userId, key, messages);
    }
    this.notify();
  }
}

// Singleton instance
const manager = new ClaudeWsManager();

// ── React Hook ─────────────────────────────────────────────────────────────

export function useClaudeSession() {
  const state = useSyncExternalStore(
    (cb) => manager.subscribe(cb),
    () => manager.state,
  );

  // Connect on first use
  useEffect(() => {
    manager.connect();
    // Don't disconnect on unmount — the manager persists
  }, []);

  const sendPrompt = useCallback(
    (
      text: string,
      projectKind?: ProjectKind,
      referenceAssetIds?: string[],
      contextEntries?: unknown[],
    ) => manager.sendPrompt(text, projectKind, referenceAssetIds, contextEntries),
    [],
  );
  const sendIterate = useCallback(
    (
      text: string,
      parentId: string,
      editedCode?: string,
      referenceAssetIds?: string[],
      fieldValues?: Record<string, unknown>,
    ) =>
      manager.sendIterate(
        text,
        parentId,
        editedCode,
        referenceAssetIds,
        fieldValues,
      ),
    [],
  );
  const sendCancel = useCallback(() => manager.sendCancel(), []);
  const sendInterrupt = useCallback((text: string) => manager.sendInterrupt(text), []);
  const sendEndSession = useCallback(() => manager.sendEndSession(), []);
  const sendNewSession = useCallback(() => manager.sendNewSession(), []);
  const loadJsonl = useCallback(() => manager.loadJsonl(), []);
  const setActiveSession = useCallback(
    (sessionId: string | null) => manager.setActiveSession(sessionId),
    [],
  );
  const promoteDraftToSession = useCallback(
    (sessionId: string) => manager.promoteDraftToSession(sessionId),
    [],
  );
  const cleanupOrphanedTranscripts = useCallback(
    (knownSessionIds: Iterable<string>, includeDraft = false) =>
      manager.cleanupOrphanedTranscripts(knownSessionIds, includeDraft),
    [],
  );
  const setMessages = useCallback(
    (messages: Message[]) => manager.setMessages(messages),
    [],
  );
  const setStyle = useCallback(
    (
      mode: "brand" | "client" | "off" | "custom",
      custom: string,
      clientId: string | null = null,
    ) => manager.setStyle(mode, custom, clientId),
    [],
  );
  const setModel = useCallback((model: string) => manager.setModel(model), []);
  const setProvider = useCallback(
    (provider: string) => manager.setProvider(provider),
    [],
  );

  return {
    ...state,
    setStyle,
    setModel,
    setProvider,
    sendPrompt,
    sendIterate,
    sendCancel,
    sendInterrupt,
    sendEndSession,
    sendNewSession,
    loadJsonl,
    setActiveSession,
    promoteDraftToSession,
    cleanupOrphanedTranscripts,
    setMessages,
  };
}
